import { ResearchTreeView, researchSummary, researchTreeView } from "@highmarch/app";
import { BalanceData, PlayerProfile, ResearchNodeDef, ResearchNodeView, ownedTowers, researchLevelOf } from "@highmarch/domain";

/**
 * The research screen's view model (M35, docs/RESEARCH.md): the tower picker strip, a tree's node cells laid out on a
 * three-column grid with the lines between them, and every string a node shows. Pure (no DOM): researchSheet.ts draws
 * it, the tests pin it. The rules are @highmarch/domain meta/research.ts; this only words and places them.
 */

/** One tile of the tower picker strip: the Fortress, then every owned tower in roster order. */
export interface ResearchTab {
  readonly towerIndex: number;
  readonly towerId: string;
  readonly name: string;
  readonly level: number;
  readonly owned: number;
  readonly total: number;
  /** Open to buy now (gate and requirements met). */
  readonly available: number;
  /** Of those, the ones the gold held covers (the red dot). */
  readonly affordable: number;
}

/** The Fortress's tree is named for the Fortress, not its archetype ("Base"). */
export function treeName(B: BalanceData, towerIndex: number): string {
  const t = B.towers[towerIndex];
  return t?.isBase ? "Fortress" : (t?.name ?? "");
}

/** The strip: the Fortress first, then each tower type owned at `highestCleared`, in roster order. */
export function researchTabs(profile: PlayerProfile, highestCleared: number): ResearchTab[] {
  const B = profile.balance, owned = ownedTowers(B, highestCleared);
  const order = [B.baseTowerIndex, ...B.towers.map((_, i) => i).filter((i) => owned[i])];
  return order.map((i) => {
    const v = researchTreeView(profile, i);
    return { towerIndex: i, towerId: B.towers[i].id, name: treeName(B, i), level: researchLevelOf(profile, i), owned: v.owned, total: v.nodes.length,
      available: v.available, affordable: v.nodes.filter((n) => n.affordable).length };
  });
}

/**
 * The tree the screen opens on: the asked tower's when it is on the strip, else the tree of the cheapest node open now,
 * else the first tower on the strip.
 */
export function initialTab(tabs: readonly ResearchTab[], profile: PlayerProfile, towerId?: string): ResearchTab | null {
  const asked = towerId === undefined ? undefined : tabs.find((t) => t.towerId === towerId);
  if (asked) return asked;
  const cheapest = researchSummary(profile).cheapestId;
  const byCheapest = cheapest === null ? undefined : tabs.find((t) => cheapest.startsWith(`${t.towerId}.`));
  return byCheapest ?? tabs.find((t) => !profile.balance.towers[t.towerIndex].isBase) ?? tabs[0] ?? null;
}

/** "+4% damage": what a node adds, short (the card's green line). */
export function effectShort(n: ResearchNodeDef): string {
  switch (n.stat) {
    case "damage": return `+${n.percent}% damage`;
    case "range": return `+${n.percent}% range`;
    case "points": return `+${n.percent}% kill points`;
    case "hp": return `+${n.percent}% fortress health`;
    case "troops": return `+${n.percent}% troop strength`;
  }
}

/** One sentence on what it does in battle, for the card's note and the toast. */
export function effectLong(B: BalanceData, n: ResearchNodeDef): string {
  const who = treeName(B, n.towerIndex);
  switch (n.stat) {
    case "damage": return n.towerIndex === B.baseTowerIndex ? `The Fortress's own shots deal ${n.percent}% more damage.` : `Every ${who} deals ${n.percent}% more damage.`;
    case "range": return `Every ${who} reaches ${n.percent}% further.`;
    case "points": return `${who} kills give ${n.percent}% more points: new cards sooner.`;
    case "hp": return `The Fortress has ${n.percent}% more health.`;
    case "troops": return `The Fortress's troops have ${n.percent}% more health and damage.`;
  }
}

/** The kit icon for a node's stat (iconsKit names). */
export function statIcon(n: ResearchNodeDef): string {
  switch (n.stat) {
    case "damage": return "sword";
    case "range": return "target";
    case "points": return "coin";
    case "hp": return "shield";
    case "troops": return "builder";
  }
}

/** Grid column: 0 left, 1 centre, 2 right. */
export type ResearchCol = 0 | 1 | 2;

/** A node placed on the tree: its row (one per tier, top to bottom) and column. */
export interface ResearchCell {
  readonly view: ResearchNodeView;
  readonly row: number;
  readonly col: ResearchCol;
  /** The tree's last node (its capstone). */
  readonly capstone: boolean;
}

/** A line from a node to one that requires it; `lit` once the child is owned or open (the path is walked). */
export interface ResearchEdge { readonly from: ResearchCell; readonly to: ResearchCell; readonly lit: boolean }

/**
 * Lays a tree out on three columns, one row per tier: a tier of two takes the left and right columns; a lone node sits
 * under its parents when they share a column, else in the centre (the capstone, which needs both branches).
 */
export function treeLayout(tree: ResearchTreeView): { cells: ResearchCell[]; edges: ResearchEdge[]; rows: number } {
  const tiers = [...new Set(tree.nodes.map((v) => v.node.tier))].sort((a, b) => a - b);
  const cells: ResearchCell[] = [];
  const last = tree.nodes[tree.nodes.length - 1]?.node.id;
  tiers.forEach((tier, row) => {
    const here = tree.nodes.filter((v) => v.node.tier === tier);
    here.forEach((view, k) => {
      let col: ResearchCol = 1;
      if (here.length >= 2) col = k === 0 ? 0 : 2;
      else {
        const parents = view.node.requires.map((r) => cells.find((c) => c.view.node.id === r)).filter((c): c is ResearchCell => c !== undefined);
        if (parents.length > 0 && parents.every((p) => p.col === parents[0].col)) col = parents[0].col;
      }
      cells.push({ view, row, col, capstone: view.node.id === last });
    });
  });
  const edges: ResearchEdge[] = [];
  for (const to of cells) for (const r of to.view.node.requires) {
    const from = cells.find((c) => c.view.node.id === r);
    if (from) edges.push({ from, to, lit: to.view.state !== "locked" });
  }
  return { cells, edges, rows: tiers.length };
}

/** The navy plate's line under the tree's name: progress, then what opens next. */
export function treeProgressLine(B: BalanceData, tree: ResearchTreeView, level: number): string {
  if (tree.owned === tree.nodes.length) return "Fully researched";
  const next = tree.nodes.find((v) => v.state === "locked" && v.lockedBy !== null && !v.lockedBy.startsWith("Needs"));
  if (tree.available > 0) return `${tree.available} ready to research`;
  return next ? `Next opens at Lv ${next.node.gate}` : "Research what it needs first";
}
